import { useEffect, useRef, useCallback, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Compass, Box, Square, Building2, Plus, Minus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getMapStyleUrl, type LatLng } from '@/api/geo';
import type { GpsPosition } from '../hooks/useGpsTracking';

interface NavigationMapProps {
  routeShape: [number, number][]; // [lon, lat] pairs - GeoJSON format
  userPosition: GpsPosition | null;
  startPoint: LatLng | null; // Draggable start point
  destination: { lat: number; lon: number };
  followUser: boolean;
  onToggleFollow?: () => void;
  onStartPointDrag?: (point: LatLng) => void;
}

export function NavigationMap({
  routeShape,
  userPosition,
  startPoint,
  destination,
  followUser,
  onStartPointDrag,
}: NavigationMapProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const userMarkerEl = useRef<HTMLDivElement | null>(null);
  const userMarker = useRef<maplibregl.Marker | null>(null);
  const startMarker = useRef<maplibregl.Marker | null>(null);
  const destMarker = useRef<maplibregl.Marker | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const onStartPointDragRef = useRef(onStartPointDrag);
  onStartPointDragRef.current = onStartPointDrag;

  // View mode states
  const [show3DBuildings, setShow3DBuildings] = useState(true); // 3D buildings visible
  const [hasPitch, setHasPitch] = useState(true); // Camera tilted
  const [isNorthUp, setIsNorthUp] = useState(false);
  const [currentBearing, setCurrentBearing] = useState(0);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current) return;

    // Initial center: user position or destination
    const initialCenter = userPosition
      ? [userPosition.lon, userPosition.lat]
      : [destination.lon, destination.lat];

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: getMapStyleUrl(),
      center: initialCenter as [number, number],
      zoom: 16,
      pitch: 45, // Start in 3D
      bearing: userPosition?.heading || 0,
      attributionControl: false,
    });

    map.current.on('load', () => {
      setMapLoaded(true);

      // Add route source and layers
      map.current!.addSource('route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: [],
          },
        },
      });

      // Route casing (outline)
      map.current!.addLayer({
        id: 'route-casing',
        type: 'line',
        source: 'route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#1e40af',
          'line-width': 8,
        },
      });

      // Route line
      map.current!.addLayer({
        id: 'route-line',
        type: 'line',
        source: 'route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#3b82f6',
          'line-width': 5,
        },
      });
    });

    // Track bearing changes
    map.current.on('rotate', () => {
      if (map.current) {
        setCurrentBearing(map.current.getBearing());
      }
    });

    return () => {
      map.current?.remove();
      map.current = null;
      setMapLoaded(false);
    };
  }, []);

  // Toggle 3D buildings visibility
  const handleToggle3DBuildings = useCallback(() => {
    if (!map.current || !mapLoaded) return;

    const newShow3D = !show3DBuildings;
    setShow3DBuildings(newShow3D);

    if (map.current.getLayer('building-3d')) {
      map.current.setLayoutProperty(
        'building-3d',
        'visibility',
        newShow3D ? 'visible' : 'none'
      );
    }
  }, [show3DBuildings, mapLoaded]);

  // Toggle pitch (camera tilt)
  const handleTogglePitch = useCallback(() => {
    if (!map.current) return;

    const newHasPitch = !hasPitch;
    setHasPitch(newHasPitch);

    map.current.easeTo({
      pitch: newHasPitch ? 45 : 0,
      duration: 300,
    });
  }, [hasPitch]);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    map.current?.zoomIn();
  }, []);

  const handleZoomOut = useCallback(() => {
    map.current?.zoomOut();
  }, []);

  // Toggle north-up / heading-up
  const handleToggleNorth = useCallback(() => {
    if (!map.current) return;

    const newIsNorthUp = !isNorthUp;
    setIsNorthUp(newIsNorthUp);

    if (newIsNorthUp) {
      // Reset to north
      map.current.easeTo({
        bearing: 0,
        duration: 300,
      });
    } else if (userPosition?.heading) {
      // Follow heading
      map.current.easeTo({
        bearing: userPosition.heading,
        duration: 300,
      });
    }
  }, [isNorthUp, userPosition?.heading]);

  // Update route line when shape changes
  useEffect(() => {
    if (!map.current || !mapLoaded || routeShape.length === 0) return;

    const source = map.current.getSource('route') as maplibregl.GeoJSONSource;
    if (source) {
      source.setData({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: routeShape,
        },
      });

      // Fit map to route bounds
      if (routeShape.length > 1) {
        const bounds = routeShape.reduce(
          (bounds, coord) => bounds.extend(coord as [number, number]),
          new maplibregl.LngLatBounds(routeShape[0], routeShape[0])
        );

        map.current.fitBounds(bounds, {
          padding: { top: 150, bottom: 200, left: 50, right: 50 },
          maxZoom: 17,
        });
      }
    }
  }, [routeShape, mapLoaded]);

  // Create/update user position marker
  const createUserMarkerElement = useCallback(() => {
    const el = document.createElement('div');
    el.innerHTML = `
      <div style="
        width: 24px;
        height: 24px;
        background-color: #3b82f6;
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        position: relative;
      ">
        <div style="
          position: absolute;
          top: -8px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-bottom: 10px solid #3b82f6;
        "></div>
      </div>
    `;

    // Add pulsing effect
    if (!document.getElementById('user-marker-pulse')) {
      const style = document.createElement('style');
      style.id = 'user-marker-pulse';
      style.textContent = `
        @keyframes user-pulse {
          0% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4); }
          70% { box-shadow: 0 0 0 20px rgba(59, 130, 246, 0); }
          100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
        }
      `;
      document.head.appendChild(style);
    }

    const innerDiv = el.querySelector('div') as HTMLElement;
    innerDiv.style.animation = 'user-pulse 2s infinite';

    return el;
  }, []);

  // Update user marker position
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    if (!userPosition) {
      // Remove marker if no position
      if (userMarker.current) {
        userMarker.current.remove();
        userMarker.current = null;
      }
      return;
    }

    if (!userMarker.current) {
      // Create new marker
      userMarkerEl.current = createUserMarkerElement();
      userMarker.current = new maplibregl.Marker({
        element: userMarkerEl.current,
        rotationAlignment: 'map',
        pitchAlignment: 'map',
      })
        .setLngLat([userPosition.lon, userPosition.lat])
        .addTo(map.current);
    } else {
      // Update position
      userMarker.current.setLngLat([userPosition.lon, userPosition.lat]);
    }

    // Update rotation based on heading (for the arrow inside marker)
    if (userPosition.heading !== null && userMarkerEl.current) {
      const innerDiv = userMarkerEl.current.querySelector('div') as HTMLElement;
      if (innerDiv) {
        // When north-up, rotate the marker itself to show heading
        // When heading-up, the map rotates, so marker points up
        if (isNorthUp) {
          innerDiv.style.transform = `rotate(${userPosition.heading}deg)`;
        } else {
          innerDiv.style.transform = 'rotate(0deg)';
        }
      }
    }

    // Follow user if enabled
    if (followUser) {
      const options: maplibregl.EaseToOptions = {
        center: [userPosition.lon, userPosition.lat],
        duration: 500,
      };

      // Only update bearing if not in north-up mode
      if (!isNorthUp && userPosition.heading !== null) {
        options.bearing = userPosition.heading;
      }

      map.current.easeTo(options);
    }
  }, [userPosition, mapLoaded, followUser, isNorthUp, createUserMarkerElement]);

  // Create/update start marker (draggable when not following)
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Determine if we should show a draggable start marker
    // Show it when followUser is off AND we have a start point
    const shouldShowDraggable = !followUser && startPoint;

    if (!shouldShowDraggable) {
      // Remove start marker if it exists
      if (startMarker.current) {
        startMarker.current.remove();
        startMarker.current = null;
      }
      return;
    }

    // Create or update start marker
    if (!startMarker.current) {
      const el = document.createElement('div');
      el.innerHTML = `
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
        ">
          <div style="
            width: 28px;
            height: 28px;
            background-color: #8b5cf6;
            border: 3px solid white;
            border-radius: 50%;
            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
            cursor: grab;
            display: flex;
            align-items: center;
            justify-content: center;
          ">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <circle cx="12" cy="12" r="3"></circle>
            </svg>
          </div>
          <div style="
            width: 0;
            height: 0;
            border-left: 8px solid transparent;
            border-right: 8px solid transparent;
            border-top: 10px solid #8b5cf6;
            margin-top: -2px;
          "></div>
        </div>
      `;

      startMarker.current = new maplibregl.Marker({
        element: el,
        anchor: 'bottom',
        draggable: true,
      })
        .setLngLat([startPoint.lon, startPoint.lat])
        .addTo(map.current);

      // Handle drag end
      startMarker.current.on('dragend', () => {
        const lngLat = startMarker.current?.getLngLat();
        if (lngLat && onStartPointDragRef.current) {
          onStartPointDragRef.current({ lat: lngLat.lat, lon: lngLat.lng });
        }
      });
    } else {
      // Update position if marker exists
      startMarker.current.setLngLat([startPoint.lon, startPoint.lat]);
    }
  }, [startPoint, followUser, mapLoaded]);

  // Create destination marker
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    if (destMarker.current) {
      destMarker.current.remove();
    }

    const el = document.createElement('div');
    el.innerHTML = `
      <div style="
        width: 32px;
        height: 32px;
        background-color: #22c55e;
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"></path>
          <line x1="4" y1="22" x2="4" y2="15"></line>
        </svg>
      </div>
    `;

    destMarker.current = new maplibregl.Marker({ element: el, anchor: 'bottom' })
      .setLngLat([destination.lon, destination.lat])
      .addTo(map.current);
  }, [destination, mapLoaded]);

  return (
    <div className="w-full h-full relative" style={{ minHeight: '300px' }}>
      <div ref={mapContainer} className="w-full h-full" />

      {/* Map controls - right side, below instruction panel */}
      <div className="absolute right-3 top-40 z-10 flex flex-col gap-2">
        {/* 3D buildings toggle */}
        <Button
          size="icon"
          variant={show3DBuildings ? 'default' : 'secondary'}
          className="h-10 w-10 shadow-lg"
          onClick={handleToggle3DBuildings}
          title={show3DBuildings ? 'Скрыть 3D здания' : 'Показать 3D здания'}
        >
          <Building2 className="w-5 h-5" />
        </Button>

        {/* Pitch (tilt) toggle */}
        <Button
          size="icon"
          variant={hasPitch ? 'default' : 'secondary'}
          className="h-10 w-10 shadow-lg"
          onClick={handleTogglePitch}
          title={hasPitch ? 'Вид сверху (2D)' : 'Наклонный вид (3D)'}
        >
          {hasPitch ? <Box className="w-5 h-5" /> : <Square className="w-5 h-5" />}
        </Button>

        {/* North-up / Heading-up toggle */}
        <Button
          size="icon"
          variant={isNorthUp ? 'default' : 'secondary'}
          className="h-10 w-10 shadow-lg"
          onClick={handleToggleNorth}
          title={isNorthUp ? 'По направлению движения' : 'Север сверху'}
          style={{
            transform: isNorthUp ? 'rotate(0deg)' : `rotate(${-currentBearing}deg)`,
            transition: 'transform 0.3s ease',
          }}
        >
          <Compass className="w-5 h-5" />
        </Button>

        {/* Zoom controls */}
        <div className="flex flex-col gap-1 mt-2">
          <Button
            size="icon"
            variant="secondary"
            className="h-10 w-10 shadow-lg"
            onClick={handleZoomIn}
          >
            <Plus className="w-5 h-5" />
          </Button>
          <Button
            size="icon"
            variant="secondary"
            className="h-10 w-10 shadow-lg"
            onClick={handleZoomOut}
          >
            <Minus className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
